// components/ReelPlayer.jsx
import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const ReelPlayer = ({ isOpen, onClose }) => {
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState('0:00');
    const [duration, setDuration] = useState('0:00');
    const [isMuted, setIsMuted] = useState(false);
    const [isPlaying, setIsPlaying] = useState(true);
    const [isVisible, setIsVisible] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const audioFadeRef = useRef(null);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Fade in audio smoothly
    const fadeInAudio = useCallback(() => {
        if (!videoRef.current) return;

        videoRef.current.volume = 0;
        let currentVolume = 0;
        const targetVolume = 0.5;
        const step = 0.02;
        const interval = 50;

        audioFadeRef.current = setInterval(() => {
            currentVolume = Math.min(currentVolume + step, targetVolume);
            if (videoRef.current) {
                videoRef.current.volume = currentVolume;
            }
            if (currentVolume >= targetVolume) {
                clearInterval(audioFadeRef.current);
            }
        }, interval);
    }, []);

    // Fade out audio smoothly
    const fadeOutAudio = useCallback(() => {
        return new Promise((resolve) => {
            if (!videoRef.current || videoRef.current.muted) {
                resolve();
                return;
            }

            let currentVolume = videoRef.current.volume;
            const step = 0.05;
            const interval = 30;

            const fade = setInterval(() => {
                currentVolume = Math.max(currentVolume - step, 0);
                if (videoRef.current) {
                    videoRef.current.volume = currentVolume;
                }
                if (currentVolume <= 0) {
                    clearInterval(fade);
                    resolve();
                }
            }, interval);
        });
    }, []);

    useEffect(() => {
        if (isOpen) {
            setIsClosing(false);
            // Trigger entrance animation
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setIsVisible(true);
                });
            });

            if (videoRef.current) {
                videoRef.current.play().then(() => {
                    fadeInAudio();
                }).catch(() => {
                    setIsMuted(true);
                    videoRef.current.muted = true;
                    videoRef.current.play();
                });
            }
        } else {
            setIsVisible(false);
        }

        return () => {
            if (audioFadeRef.current) {
                clearInterval(audioFadeRef.current);
            }
            if (videoRef.current) {
                videoRef.current.pause();
            }
        };
    }, [isOpen, fadeInAudio]);

    const handleClose = useCallback(async () => {
        setIsClosing(true);
        setIsVisible(false);
        await fadeOutAudio();

        // Wait for exit animation
        setTimeout(() => {
            onClose();
            setIsClosing(false);
        }, 500);
    }, [onClose, fadeOutAudio]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') handleClose();
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = '';
        };
    }, [isOpen, handleClose]);

    const handleTimeUpdate = useCallback(() => {
        if (videoRef.current) {
            const current = videoRef.current.currentTime;
            const total = videoRef.current.duration;
            setProgress((current / total) * 100);
            setCurrentTime(formatTime(current));
        }
    }, []);

    const handleLoadedMetadata = useCallback(() => {
        if (videoRef.current) {
            setDuration(formatTime(videoRef.current.duration));
            setIsLoaded(true);
        }
    }, []);

    const handleProgressClick = useCallback((e) => {
        if (videoRef.current) {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const percentage = x / rect.width;
            videoRef.current.currentTime = percentage * videoRef.current.duration;
        }
    }, []);

    const toggleMute = useCallback(() => {
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setIsMuted(videoRef.current.muted);
        }
    }, []);

    const togglePlay = useCallback(() => {
        if (videoRef.current) {
            if (videoRef.current.paused) {
                videoRef.current.play();
                setIsPlaying(true);
            } else {
                videoRef.current.pause();
                setIsPlaying(false);
            }
        }
    }, []);

    if (!isOpen && !isClosing) return null;

    return createPortal(
        <div
            ref={containerRef}
            className={`fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-500 ease-out ${isVisible ? 'opacity-100' : 'opacity-0'}`}
            style={{ cursor: 'auto' }}
        >
            {/* Backdrop */}
            <div
                className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-all duration-700 ease-out ${isVisible ? 'backdrop-blur-sm' : 'backdrop-blur-none'}`}
                onClick={handleClose}
            />

            {/* Content */}
            <div className={`relative w-full h-full flex flex-col items-center justify-center p-4 md:p-8 lg:p-12 transition-all duration-700 ease-out ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
                {/* Close button */}
                <button
                    onClick={handleClose}
                    className="absolute top-6 right-6 md:top-8 md:right-8 z-10 group"
                    aria-label="Close"
                >
                    <div className="relative w-12 h-12 flex items-center justify-center">
                        <div className="absolute inset-0 border border-white/20 rounded-full transition-all duration-300 group-hover:border-white/40 group-hover:scale-110" />
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            className="transition-transform duration-300 group-hover:rotate-90"
                        >
                            <path
                                d="M1 1L15 15M15 1L1 15"
                                stroke="white"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                            />
                        </svg>
                    </div>
                </button>

                {/* Title */}
                <div className={`absolute top-6 left-6 md:top-8 md:left-8 z-10 transition-all duration-700 delay-200 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
                    <p className="text-white/40 text-[10px] uppercase tracking-[0.3em] mb-1">Showreel</p>
                    <p className="text-white/80 text-sm font-light tracking-wide">2025</p>
                </div>

                {/* Video container */}
                <div className={`relative w-full max-w-[90vw] md:max-w-[85vw] lg:max-w-[80vw] xl:max-w-[75vw] aspect-video transition-all duration-700 delay-100 ease-out ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-[0.98]'}`}>
                    {/* Loading state */}
                    {!isLoaded && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                            <div className="w-8 h-8 border border-white/20 border-t-white/80 rounded-full animate-spin" />
                        </div>
                    )}

                    {/* Video */}
                    <video
                        ref={videoRef}
                        src="/video/reel.mp4"
                        className="w-full h-full object-contain"
                        playsInline
                        onTimeUpdate={handleTimeUpdate}
                        onLoadedMetadata={handleLoadedMetadata}
                        onEnded={() => setIsPlaying(false)}
                        onClick={togglePlay}
                    />

                    {/* Play/Pause overlay */}
                    {!isPlaying && isLoaded && (
                        <div
                            className="absolute inset-0 flex items-center justify-center cursor-pointer"
                            onClick={togglePlay}
                        >
                            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full border border-white/30 flex items-center justify-center backdrop-blur-sm bg-white/5 transition-transform duration-300 hover:scale-110">
                                <svg width="24" height="28" viewBox="0 0 24 28" fill="none" className="ml-1">
                                    <path d="M2 2L22 14L2 26V2Z" fill="white" fillOpacity="0.9" />
                                </svg>
                            </div>
                        </div>
                    )}
                </div>

                {/* Controls */}
                <div className={`absolute bottom-6 left-6 right-6 md:bottom-8 md:left-8 md:right-8 lg:bottom-12 lg:left-12 lg:right-12 transition-all duration-700 delay-300 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                    <div className="flex items-center gap-4 md:gap-6">
                        {/* Play/Pause */}
                        <button
                            onClick={togglePlay}
                            className="w-10 h-10 flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity"
                            aria-label={isPlaying ? 'Pause' : 'Play'}
                        >
                            {isPlaying ? (
                                <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
                                    <rect x="0" y="0" width="4" height="16" fill="white" />
                                    <rect x="10" y="0" width="4" height="16" fill="white" />
                                </svg>
                            ) : (
                                <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
                                    <path d="M0 0L14 8L0 16V0Z" fill="white" />
                                </svg>
                            )}
                        </button>

                        {/* Time */}
                        <div className="text-white/50 text-xs font-mono tracking-wide min-w-[80px]">
                            <span className="text-white/80">{currentTime}</span>
                            <span className="mx-1">/</span>
                            <span>{duration}</span>
                        </div>

                        {/* Progress bar */}
                        <div
                            className="flex-1 h-10 flex items-center cursor-pointer group"
                            onClick={handleProgressClick}
                        >
                            <div className="w-full h-[2px] bg-white/10 relative">
                                <div
                                    className="absolute left-0 top-0 h-full bg-white/80 transition-all duration-100"
                                    style={{ width: `${progress}%` }}
                                />
                                <div
                                    className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                                    style={{ left: `${progress}%`, transform: `translate(-50%, -50%)` }}
                                />
                            </div>
                        </div>

                        {/* Mute */}
                        <button
                            onClick={toggleMute}
                            className="w-10 h-10 flex items-center justify-center opacity-60 hover:opacity-100 transition-opacity"
                            aria-label={isMuted ? 'Unmute' : 'Mute'}
                        >
                            {isMuted ? (
                                <svg width="20" height="18" viewBox="0 0 20 18" fill="none">
                                    <path d="M10 1L5 5H1V13H5L10 17V1Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M15 6L19 12M19 6L15 12" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                            ) : (
                                <svg width="24" height="18" viewBox="0 0 24 18" fill="none">
                                    <path d="M10 1L5 5H1V13H5L10 17V1Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M15 5.5C16.5 6.5 17 8.5 17 9C17 9.5 16.5 11.5 15 12.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                                    <path d="M18 3C20.5 5 21.5 7.5 21.5 9C21.5 10.5 20.5 13 18 15" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>

                {/* ESC hint */}
                <div className={`absolute bottom-6 left-1/2 -translate-x-1/2 md:bottom-8 transition-all duration-700 delay-500 ease-out ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
                    <p className="text-white/20 text-[10px] uppercase tracking-[0.2em]">
                        Press ESC to close
                    </p>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ReelPlayer;
